/**
 * Realtime client.
 *
 * One socket per browser tab. Incoming events are applied directly to the
 * TanStack Query cache rather than triggering refetches: an agent changing a
 * status should show up on a colleague's screen immediately, and a refetch
 * round trip per event would both lag and stampede the API when several agents
 * are working the same queue.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";

import type {
  Message,
  PresenceEntry,
  PresenceState,
  TicketActivity,
  TicketDetail,
  TicketSummary,
} from "../types";
import { queryKeys } from "./queryKeys";

interface SocketContextValue {
  socket: Socket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
});

/**
 * How long to wait for more ticket changes before re-asking for the lists.
 *
 * Long enough that a bulk action arrives as one refetch, short enough that a
 * ticket a colleague just closed is gone from Open before anybody reaches for
 * it. The row is already showing its new status throughout, so this is only
 * the delay on it leaving.
 */
const LIST_REFRESH_DEBOUNCE_MS = 1_000;

/**
 * The longest a refresh can be put off by events that keep arriving.
 *
 * A pure debounce restarts on every event, so a busy minute of inbound mail
 * would hold the lists stale for as long as it lasted. This caps it: the
 * first event starts a clock, and the refresh happens by this deadline
 * however many more arrive.
 */
const LIST_REFRESH_MAX_WAIT_MS = 3_000;

export function SocketProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const socket = useMemo(() => {
    /*
     * Same origin, so no URL. Auth is the session cookie, sent automatically on
     * the handshake -- there is no token to pass, which also means no token to
     * leak into logs or a query string.
     */
    const instance = io({
      path: "/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });
    socketRef.current = instance;
    return instance;
  }, []);

  /*
   * Coalesces every refetch a ticket event asks for: the lists, and the view
   * counts beside them.
   *
   * Without it, a colleague bulk-closing thirty tickets is thirty broadcasts to
   * every agent on the desk and thirty list queries behind them. With it, that
   * is one -- and the delay is invisible next to the row having been correct
   * in place the whole time, because the patch has already landed.
   *
   * New mail goes through here too. It used to refetch on the spot, and the
   * API announces mail one message at a time, so a Gmail sync that brought in
   * four was four refetches of every loaded page from every agent at once --
   * forty-odd copies of the same queue query inside two seconds, queued
   * behind the API's database pool. Batched, it is one per agent.
   *
   * `lists` is sticky across the window: a counts-only event never cancels a
   * list refresh an earlier event asked for.
   */
  const pendingRefresh = useRef<{
    timer: number;
    firstAt: number;
    lists: boolean;
  } | null>(null);

  const scheduleRefresh = useCallback(
    ({ lists }: { lists: boolean }) => {
      const now = Date.now();
      const pending = pendingRefresh.current;
      if (pending) window.clearTimeout(pending.timer);

      const firstAt = pending?.firstAt ?? now;
      const wantLists = lists || (pending?.lists ?? false);
      const delay = Math.max(
        0,
        Math.min(LIST_REFRESH_DEBOUNCE_MS, firstAt + LIST_REFRESH_MAX_WAIT_MS - now),
      );

      pendingRefresh.current = {
        firstAt,
        lists: wantLists,
        timer: window.setTimeout(() => {
          pendingRefresh.current = null;
          void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
          if (wantLists) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
          }
        }, delay),
      };
    },
    [queryClient],
  );

  useEffect(
    () => () => {
      if (pendingRefresh.current) window.clearTimeout(pendingRefresh.current.timer);
    },
    [],
  );

  useEffect(() => {
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    /*
     * A new ticket is refetched rather than spliced in.
     *
     * Only the server knows whether it belongs in the list on screen: these
     * events reach every agent, and "unassigned", "my open tickets" and any
     * saved view all have filters this client would have to re-implement to
     * decide. Guessing wrong puts somebody else's ticket in your queue.
     */
    const onTicketCreated = () => scheduleRefresh({ lists: true });

    const onTicketUpdated = (ticket: TicketSummary) => {
      const moved = patchTicketInLists(queryClient, ticket);

      queryClient.setQueryData<TicketDetail>(
        queryKeys.ticket(ticket.id),
        (previous) => (previous ? { ...previous, ticket } : previous),
      );

      /*
       * A ticket somebody else closed has to leave the Open list, not sit in it
       * labelled "closed".
       *
       * Patching alone could never do that -- it rewrites the row where it
       * stands and this client cannot re-evaluate a saved view's filters to
       * decide the row should go. Closing a ticket yourself already refetched
       * the list, which is why the bug only ever showed for a colleague's
       * close. This asks the server the same question, on the same events.
       */
      scheduleRefresh({ lists: moved });
    };

    /*
     * New customer mail. The ticket.updated broadcast already moved the row and
     * refreshed its timestamp; this refetches the list for the one thing that
     * event cannot carry -- which tickets are unread, which is per agent and so
     * is answered alongside the list rather than on the shared payload.
     */
    const onMailArrived = () => scheduleRefresh({ lists: true });

    const onMessageCreated = (message: MessageEvent_) => {
      /*
       * The socket payload is a summary, not the full message row (it carries
       * no body). Refetching the detail is correct here rather than patching:
       * the conversation needs the body, attachments and sanitised HTML, none
       * of which the event contains.
       */
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(message.ticketId),
      });
    };

    const onMessageUpdated = (message: MessageEvent_) => {
      /* A reply failing, or being sent again, changes the banner across the
         top of every page; it polls, but there is no reason to wait for it. */
      if (message.delivery === "failed" || message.deliveryError === null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.channelAlerts });
      }

      // Delivery state changed (queued -> sent/failed). Patch in place so the
      // composer's optimistic entry flips without the whole thread flickering.
      queryClient.setQueryData<TicketDetail>(
        queryKeys.ticket(message.ticketId),
        (previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            messages: previous.messages.map((existing) =>
              existing.id === message.id
                ? {
                    ...existing,
                    delivery: message.delivery,
                    /* Only when the event carries it: a "sent" event says
                       nothing about the error, a failure or a retry does. */
                    ...(message.deliveryError !== undefined && {
                      deliveryError: message.deliveryError,
                    }),
                  }
                : existing,
            ),
          };
        },
      );
    };

    const onActivity = (activity: TicketActivity) => {
      queryClient.setQueryData<TicketDetail>(
        queryKeys.ticket(activity.ticketId),
        (previous) => {
          if (!previous) return previous;
          // Guard against the duplicate that arrives when this client caused
          // the change and already applied it optimistically.
          if (previous.activity.some((entry) => entry.id === activity.id)) {
            return previous;
          }
          return { ...previous, activity: [...previous.activity, activity] };
        },
      );
    };

    /**
     * Somebody claimed a call, or put one back.
     *
     * Broadcast to the whole desk, so this fires for everyone *except* the
     * person who did it -- which is the point. The group sections on the sales
     * board are panels people leave open all day, and before this a claim was
     * invisible until you collapsed one and opened it again; two reps working
     * the same group would ring the same company.
     *
     * Invalidates rather than patching the row. The claim moves the pool
     * count, the group counts and the worklist order as well as the row, and
     * keeping four caches in step by hand is how they drift apart.
     */
    const onSalesClaim = (): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.salesRoot });
    };

    socket.on("sales.claim", onSalesClaim);
    socket.on("ticket.created", onTicketCreated);
    socket.on("ticket.updated", onTicketUpdated);
    socket.on("message.created", onMessageCreated);
    socket.on("message.updated", onMessageUpdated);
    socket.on("mail.arrived", onMailArrived);
    socket.on("ticket.activity", onActivity);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("sales.claim", onSalesClaim);
      socket.off("ticket.created", onTicketCreated);
      socket.off("ticket.updated", onTicketUpdated);
      socket.off("message.created", onMessageCreated);
      socket.off("message.updated", onMessageUpdated);
      socket.off("mail.arrived", onMailArrived);
      socket.off("ticket.activity", onActivity);
    };
  }, [socket, queryClient, scheduleRefresh]);

  useEffect(() => () => void socketRef.current?.close(), []);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

interface MessageEvent_ {
  id: number;
  ticketId: number;
  delivery: Message["delivery"];
  deliveryError?: string | null;
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}

/*
 * There is deliberately no channel-room hook.
 *
 * One existed and nothing ever called it, which is what broke live updates: the
 * server addressed every ticket.created / ticket.updated to a room no client
 * had joined. Views span channels -- "unassigned", "my open tickets", a saved
 * view over three queues -- so there is no single channel id a list could
 * subscribe with. Ticket changes are broadcast to every agent instead; see
 * RealtimeService.ticketUpdated.
 */

/**
 * Presence heartbeat interval.
 *
 * Must be comfortably under the server's 45s TTL, otherwise an agent who is
 * still on the ticket flickers out of the presence list between beats.
 */
const PRESENCE_HEARTBEAT_MS = 15_000;

/**
 * Join a ticket's room and publish presence.
 *
 * `state` moves to "replying" as soon as the agent starts typing, which is the
 * signal that actually prevents two people writing the same answer.
 */
export function useTicketRoom(
  ticketId: number | undefined,
  state: PresenceState,
): PresenceEntry[] {
  const { socket, connected } = useSocket();
  const [presence, setPresence] = useState<PresenceEntry[]>([]);

  useEffect(() => {
    if (!socket || !connected || ticketId === undefined) return;

    socket.emit("ticket.subscribe", ticketId);

    const onPresence = (payload: { ticketId: number; entries: PresenceEntry[] }) => {
      if (payload.ticketId === ticketId) setPresence(payload.entries);
    };
    socket.on("presence.updated", onPresence);

    return () => {
      socket.off("presence.updated", onPresence);
      socket.emit("ticket.unsubscribe", ticketId);
      setPresence([]);
    };
  }, [socket, connected, ticketId]);

  useEffect(() => {
    if (!socket || !connected || ticketId === undefined) return;

    const beat = () => socket.emit("presence.set", { ticketId, state });
    beat();

    const timer = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [socket, connected, ticketId, state]);

  return presence;
}

/**
 * How react-query holds a ticket list.
 *
 * The list is an infinite query, so the cache entry is *pages* of what the
 * endpoint returned rather than one response, and both patchers below have to
 * walk them. Worth naming as a type: when the list was a single fetch these
 * functions read `previous.tickets` directly, and the shape changing under them
 * is not something TypeScript would have caught on its own -- `setQueriesData`
 * is told the shape by its caller, so a stale one type-checks perfectly and
 * then silently matches nothing at runtime.
 */
interface TicketListPage {
  tickets: TicketSummary[];
  nextCursor: number | null;
  unreadIds: number[];
}

interface CachedTicketList {
  pages: TicketListPage[];
  pageParams: unknown[];
}

/**
 * Apply a ticket update to every cached list that already holds that ticket.
 *
 * Deliberately never inserts. A list the ticket is absent from is one whose
 * filters it did not match when the server built it, and this client cannot
 * re-evaluate those filters -- so adding it would show a closed ticket under
 * "open", or somebody else's under "mine". The row is left to the next fetch.
 *
 * The counterpart risk, a ticket that has just become eligible for the view,
 * resolves on the next refetch too. Showing a stale list briefly is a much
 * smaller error than showing a wrong one.
 *
 * For the same reason it never reorders. Ordering is decided by the server from
 * the sort and direction the agent picked, and the list panes render that order
 * verbatim -- so re-sorting here by recency threw away their choice, and any
 * ticket changing anywhere snapped a queue sorted by SLA, subject or assignee
 * back to newest-first. The row is updated where it stands; the next fetch
 * moves it if it has genuinely moved.
 *
 * Returns whether the change was one that could decide membership, so the
 * caller knows to go and ask. Deliberately a "might this matter" test and not
 * an attempt to evaluate the filters: it is answered from the three fields
 * every view is built out of, and being wrong costs one refetch that changes
 * nothing, where the old behaviour of never asking cost a closed ticket
 * sitting in Open until something else happened to refresh the list.
 */
function patchTicketInLists(
  queryClient: QueryClient,
  ticket: TicketSummary,
): boolean {
  let moved = false;

  queryClient.setQueriesData<CachedTicketList>(
    { queryKey: queryKeys.ticketsRoot },
    (previous) => {
      if (!previous?.pages) return previous;

      let found = false;

      const pages = previous.pages.map((page) => {
        const index = page.tickets.findIndex((entry) => entry.id === ticket.id);
        if (index === -1) return page;

        found = true;

        const before = page.tickets[index]!;
        if (
          before.status !== ticket.status ||
          before.assigneeId !== ticket.assigneeId ||
          before.pipelineId !== ticket.pipelineId
        ) {
          moved = true;
        }

        const next = [...page.tickets];
        next[index] = ticket;

        return { ...page, tickets: next };
      });

      // The same identity back when nothing matched, so an unrelated ticket
      // updating somewhere does not re-render every cached list.
      return found ? { ...previous, pages } : previous;
    },
  );

  return moved;
}

/**
 * Drop a ticket from every cached list's unread set.
 *
 * Opening a ticket marks it read on the server, but the list is a separate
 * query that does not hear about it -- so the row went back to bold the moment
 * the agent moved on, and stayed bold until the list happened to refetch. That
 * is the "comes off bold a few minutes later" report: nothing was wrong with
 * the reading, only with when the list was told.
 *
 * Applied to every cached list rather than the current one, because the same
 * ticket appears in several: Open, Mine, a saved view, and the search results
 * the agent came from.
 */
export function markTicketReadInLists(
  queryClient: QueryClient,
  ticketId: number,
): void {
  queryClient.setQueriesData<CachedTicketList>(
    { queryKey: queryKeys.ticketsRoot },
    (previous) => {
      if (!previous?.pages) return previous;

      let found = false;

      const pages = previous.pages.map((page) => {
        if (!page.unreadIds.includes(ticketId)) return page;

        found = true;
        return {
          ...page,
          unreadIds: page.unreadIds.filter((id) => id !== ticketId),
        };
      });

      return found ? { ...previous, pages } : previous;
    },
  );
}
